import { Controller } from "@hotwired/stimulus";
import { copyText } from "../lib/ClipboardFunctions";
import flash from "../lib/flashes.js";

export default class extends Controller {
  static targets = ["value", "progress", "expired", "buttons", "revealButton", "copyButton", "refreshButton"];
  static values = {
    url: String
  };

  connect() {
    this.resetState();
    this.renderInitialState();
  }

  resetState() {
    this.isShown = false;
    this.active = false;
    this.otp = "";
    this.isCopied = false;
    this.count = 30;
    this.error = false;
    this.isLoading = false;
  }

  renderInitialState() {
    this.element.innerHTML = `
      <div class='button-group'>
        <a href='#' data-otp-generator-target="revealButton" data-action="click->otp-generator#open" class='button button--secondary'>
          <i class='fas fa-eye'></i>Reveal
        </a>
        <a href='#' data-otp-generator-target="copyButton" data-action="click->otp-generator#copyWithoutView" class='button button--secondary'>
          <i class='fas fa-copy'></i>Copy
        </a>
      </div>
    `;
  }

  open() {
    this.isShown = true;
    this.active = true;
    this.isLoading = true;
    this.renderLoadingState();
    this.lookup();
  }

  async lookup() {
    try {
      const response = await fetch(this.urlValue);
      const data = await response.json();

      if (data.otp) {
        this.count = data.time_left;
        this.otp = data.otp;
        this.isLoading = false;
        this.renderActiveState();

        this.countdown = setInterval(() => {
          this.count--;
          if (this.count <= 0) {
            clearInterval(this.countdown);
            this.resetState();
            this.renderInitialState();
          } else {
            this.updateProgress();
          }
        }, 1000);
      } else {
        this.error = true;
        this.isLoading = false;
        this.renderErrorState();
      }
    } catch (error) {
      this.error = true;
      this.isLoading = false;
      this.renderErrorState();
    }
  }

  copy() {
    this.setClipboard(this.otp);
    flash("Copied to clipboard", "success");
  }

  copyWithoutView() {
    try {
      // Use synchronous XMLHttpRequest for Safari compatibility
      const xhr = new XMLHttpRequest();
      xhr.open("GET", this.urlValue, false); // false = synchronous
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.send();

      const data = JSON.parse(xhr.responseText);

      this.setClipboard(data.otp);
      flash("Copied to clipboard", "success");
    } catch (error) {
      this.error = true;
      flash("Failed to copy OTP", "error");
      this.renderErrorState();
    }
  }

  setClipboard(value) {
    copyText(value);
    this.isCopied = true;
    this.updateCopyButton();
    setTimeout(() => {
      this.isCopied = false;
      this.updateCopyButton();
    }, 2000);
  }

  renderLoadingState() {
    this.element.innerHTML = `
      <div class='otp'>
        <span data-otp-generator-target="value" class='otp__value'>
          <i class='fas fa-spinner fa-spin'></i> Loading...
        </span>
      </div>
    `;
  }

  renderActiveState() {
    this.element.innerHTML = `
      <div class='otp'>
        <span data-otp-generator-target="value" class='otp__value'>${this.otp}</span>
        <div data-otp-generator-target="progress" class='progress' style='width: ${this.countPercentage}%'></div>
        <nav>
          <div style='display: inline-block'>
            <a href='#' data-otp-generator-target="copyButton" data-action="click->otp-generator#copy" class='button button--secondary'>
              <i class='fas fa-copy'></i>Copy
            </a>
          </div>
        </nav>
      </div>
    `;
  }

  renderErrorState() {
    this.element.innerHTML = `
      <div class='otp'>
        <span data-otp-generator-target="value" class='otp__value danger'>ERROR</span>
        <nav>
          <div style='display: inline-block'>
            <a href='#' data-otp-generator-target="refreshButton" data-action="click->otp-generator#open" class='button button--secondary'>
              <i class='fas fa-redo'></i>Refresh
            </a>
          </div>
        </nav>
      </div>
    `;
  }

  updateProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${this.countPercentage}%`;
    }
  }

  updateCopyButton() {
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.innerHTML = `<i class='fas fa-copy'></i>${this.isCopied ? 'Copied!' : 'Copy'}`;
    }
  }

  get countPercentage() {
    return ((1 - (this.count / 30)) * 100);
  }
}
